'use client';

import Image from 'next/image';
import React, { type RefCallback, useEffect, useMemo } from 'react';

import { Color } from '@bytel/product-wall';
import { classNames } from '@bytel/product-wall/helpers';
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Align,
    Box,
    BoxContent,
    Breadcrumb,
    BreadcrumbItem,
    Button,
    Column,
    Columns,
    Container,
    Divider,
    FlexBox,
    Icon,
    IconName,
    IconSize,
    Justify,
    RadioTile,
    RadioTiles,
    Row,
    Rows,
    Section,
    Spacer,
    SpacerSize,
    Tab,
    TabList,
    Tabs,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyBold,
    TypographyColor,
    VariantState,
    View,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';

import { WallType } from '@app-types/config';
import { SalesWallType } from '@app-types/sales';

import { useOnScreen } from '@hooks/use-on-screen';

import { paths } from '@helpers/path';

import TradeInIcon from '@assets/logo-trade-in.svg';

import { BackLink } from './back-link';
import { CategoryBox } from './components';
import { Reinsurance } from './reinsurance';

const config = getAppConfig();

function generateEmptyArray(length: number): number[] {
    return Array.from(
        {
            length,
        },
        (_, k) => k,
    );
}

export type ProductSkeletonProps = {
    isRenewal?: boolean;
    type: WallType;
    baseUrl: string;
};

export function ProductSkeleton({ isRenewal = false, type, baseUrl }: Readonly<ProductSkeletonProps>) {
    const { isVisible: isTicketVisible, ref: ticketRef } = useOnScreen();
    const { isVisible: isStickyVisible, ref: stickyRef } = useOnScreen();

    useEffect(() => {
        // loading component keeps the scroll position for a strange reason. This is a workaround to fix it
        window.scrollTo(0, 0);
    }, []);

    return (
        <Section backgroundColor={TrilogyColor.NEUTRAL_FADE} className='is-paddingless' data-type={type}>
            <Section backgroundColor={TrilogyColor.BACKGROUND} className='is-paddingless'>
                <BackLink baseUrl={baseUrl} type={type} />
            </Section>
            <Spacer size={SpacerSize.SIX} />
            <SkeletonSticky isTicketShowed={isTicketVisible && !isStickyVisible} showRef={stickyRef} type={type} />
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container medium className='product-page-layout'>
                    <View className='is-loading is-flex-col-touch product-title'>
                        <div className='has-text-centered-touch'>
                            <Text style={{ fontSize: '24px' }}>Lorem ipsum dolor sit</Text>
                            <Text style={{ fontSize: '12px' }}>Lorem ipsum</Text>
                        </div>
                    </View>
                    <SkeletonPreview type={type} />
                    <div className='product-characteristics'>
                        <Spacer size={SpacerSize.FOUR} />
                        <SkeletonCustomization type={type} />
                        {(type === WallType.PHONE || type === WallType.HOTSPOT) && (
                            <SkeletonPlan type={type} isRenewal={isRenewal} />
                        )}
                        {type === WallType.PHONE && <SkeletonOffers />}
                        <SkeletonTicket isRenewal={isRenewal} type={type} showRef={ticketRef} />
                    </div>
                </Container>
                <Container medium>
                    <Spacer size={SpacerSize.SIX} />
                    <Reinsurance isRenewal={isRenewal} />
                </Container>
            </Section>
            <Section>
                <SkeletonTabs />
            </Section>
            <Section>
                <Container>
                    <SkeletonBreadcrumb type={type} isRenewal={isRenewal} />
                    <SkeletonLegals />
                </Container>
            </Section>
        </Section>
    );
}

export function SkeletonSticky({
    isTicketShowed,
    type,
    showRef,
}: {
    isTicketShowed: boolean;
    type: WallType;
    showRef: RefCallback<HTMLDivElement>;
}) {
    const hasPlan = type === WallType.PHONE || type === WallType.HOTSPOT;

    const productIcon = useMemo(() => {
        switch (type) {
            case WallType.PHONE:
            case WallType.SALES_PHONE:
                return IconName.MOBILE;
            case WallType.ACCESSORY:
            case WallType.SALES_ACCESSORY:
                return IconName.ACCESSORIES;
            case WallType.HOTSPOT:
                return IconName.HOTSPOT;
            default:
                return IconName.ENERGY;
        }
    }, [type]);

    return (
        <>
            <Container
                className={classNames('sticky-box is-sticky-top is-backgroundless', isTicketShowed ? 'hidden' : '')}
            >
                <Box>
                    <Columns mobile verticalAlign={Align.CENTER} marginless>
                        <Column>
                            <View className='gap-huge is-flex-tablet'>
                                <View
                                    flexable
                                    align={Align.CENTER}
                                    style={{ maxWidth: !hasPlan ? '40rem' : 'unset' }}
                                    className='gap-medium w-fit'
                                >
                                    <Icon
                                        name={productIcon}
                                        size={IconSize.MEDIUM}
                                        className='is-hidden-tablet-only w-fit'
                                    />
                                    <div className='is-loading'>
                                        <Text
                                            typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}
                                            style={{ fontSize: '30px', letterSpacing: '0.6rem' }}
                                        >
                                            Rim ZWINA
                                        </Text>
                                    </div>
                                </View>
                                {hasPlan && (
                                    <View
                                        flexable
                                        align={Align.CENTER}
                                        style={{ maxWidth: '40rem' }}
                                        className='gap-medium w-fit'
                                    >
                                        <Icon
                                            name={IconName.SIM_CARD}
                                            size={IconSize.MEDIUM}
                                            className='is-hidden-tablet-only w-fit'
                                        />
                                        <div className='is-loading'>
                                            <Text
                                                typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}
                                                style={{ fontSize: '30px' }}
                                            >
                                                Félix le BG
                                            </Text>
                                        </div>
                                    </View>
                                )}
                            </View>
                        </Column>
                        <Column narrow>
                            <Button disabled>
                                <Text className='is-hidden-mobile is-invisible' marginless>
                                    Ajouter au panier
                                </Text>
                                <Icon name={IconName.FRANCE_MARKER} className='is-hidden-tablet is-invisible' />
                            </Button>
                        </Column>
                    </Columns>
                </Box>
            </Container>
            <div ref={showRef} />
        </>
    );
}

export function SkeletonPreview({ type }: Readonly<{ type: WallType }>) {
    const isAccessory = type === WallType.ACCESSORY || type === WallType.SALES_ACCESSORY;

    const imageSkeleton = !isAccessory
        ? `${config.assets.cmsImages.url}/original/image/CMS/ecommerce/wall/lazyload-phone-default-image.svg`
        : `${config.assets.cmsImages.url}/original/image/CMS/ecommerce/wall/lazyload-accessory-default-image.svg`;

    return (
        <div className='is-sticky-top-desktop product-preview'>
            <Columns align={Align.CENTER} mobile marginless>
                <div className='is-relative'>
                    <div className='product-thumbnail'>
                        <Column narrow verticalAlign={Align.CENTER}>
                            <View flexable className='gap-medium is-flex-col' align={Align.CENTER}>
                                <Icon
                                    name={IconName.ARROW_UP}
                                    className={`cursor-pointer ${!isAccessory ? 'is-invisible' : ''}`}
                                />
                                {generateEmptyArray(3).map((value) => (
                                    <div className='cursor-pointer' key={value}>
                                        <Image
                                            key={value}
                                            src={imageSkeleton}
                                            alt=''
                                            width={40}
                                            height={!isAccessory ? 80 : 40}
                                        />
                                    </div>
                                ))}
                                <Icon
                                    name={IconName.ARROW_DOWN}
                                    className={`cursor-pointer ${!isAccessory ? 'is-invisible' : ''}`}
                                />
                            </View>
                        </Column>
                    </div>
                    <Column>
                        <Image src={imageSkeleton} alt='' width={200} height={!isAccessory ? 400 : 200} />
                    </Column>
                </div>
            </Columns>
            <Spacer size={SpacerSize.FOUR} />
            <View flexable justify={Justify.CENTER} className='gap-medium'>
                {generateEmptyArray(type === WallType.PHONE || type === WallType.SALES_PHONE ? 4 : 1).map((value) => (
                    <Color color='#ececec' key={value} />
                ))}
            </View>
            {!isAccessory && (
                <>
                    <Spacer size={SpacerSize.FOUR} />
                    <div className='is-loading is-flex is-aligned-center is-flex-col'>
                        <Text marginless>DAS tête : 0,980x</Text>
                        <Spacer size={SpacerSize.ONE} />
                        <Text marginless>DAS tronc : 0,960x</Text>
                        <Spacer size={SpacerSize.ONE} />
                        <Text marginless>DAS membres : 2,990x</Text>
                    </div>
                </>
            )}
        </div>
    );
}

function SkeletonCustomization({ type }: Readonly<{ type: WallType }>) {
    const isPhone = type === WallType.PHONE || type === WallType.SALES_PHONE;

    const title = useMemo(() => {
        switch (type) {
            case WallType.HOTSPOT:
                return 'Je personnalise mon hotspot';
            case WallType.ACCESSORY:
            case WallType.SALES_ACCESSORY:
                return 'Je personnalise mon accessoire';
            case WallType.PHONE:
            case WallType.SALES_PHONE:
            default:
                return 'Quel modèle voulez-vous\u00A0?';
        }
    }, [type]);

    return (
        <CategoryBox title={title}>
            <Rows gap={5}>
                <Row>
                    <Text
                        level={TextLevels.ONE}
                        typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}
                    >
                        La couleur
                    </Text>
                    <RadioTiles className='grid-radio-list is-loading'>
                        {generateEmptyArray(isPhone ? 4 : 1).map((value) => (
                            <RadioTile id={`color-${value}`} key={value} label='Lorem' disabled horizontal />
                        ))}
                    </RadioTiles>
                </Row>
                {isPhone && (
                    <Row>
                        <Text
                            level={TextLevels.ONE}
                            typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}
                        >
                            La mémoire
                        </Text>
                        <RadioTiles className='grid-radio-list is-loading'>
                            {generateEmptyArray(3).map((value) => (
                                <RadioTile id={`memory-${value}`} key={value} label='128 Go' disabled horizontal />
                            ))}
                        </RadioTiles>
                    </Row>
                )}
                <SkeletonStock />
            </Rows>
        </CategoryBox>
    );
}

export function SkeletonStock() {
    return (
        <Row>
            <FlexBox>
                <Icon name={IconName.CHECK} className='is-loading' />
                <div className='is-loading'>
                    <Text typo={TypographyColor.TEXT_MAIN} level={TextLevels.TWO}>
                        En stock en ligne, livré dès
                    </Text>
                </div>
            </FlexBox>
        </Row>
    );
}

export function SkeletonPlan({ type, isRenewal }: Readonly<{ type: WallType; isRenewal: boolean }>) {
    return (
        <>
            {type === WallType.PHONE && (
                <>
                    {!isRenewal && (
                        <CategoryBox title='Avec ou sans forfait&nbsp;?'>
                            <RadioTiles>
                                <RadioTile
                                    horizontal
                                    label='Avec forfait'
                                    description={
                                        <Text skeleton>
                                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
                                            tempor incididunt ut labore et dolore magna aliqua.
                                        </Text>
                                    }
                                    disabled
                                    checked
                                />
                                <RadioTile
                                    horizontal
                                    label='Sans forfait'
                                    description={
                                        <Text skeleton>
                                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
                                            tempor incididunt ut labore et dolore magna aliqua.
                                        </Text>
                                    }
                                    disabled
                                />
                            </RadioTiles>
                        </CategoryBox>
                    )}

                    <CategoryBox title='Comment voulez-vous payer votre téléphone&nbsp;?'>
                        <RadioTiles>
                            <RadioTile
                                horizontal
                                sticker='Nouveau'
                                stickerVariant={VariantState.MAIN}
                                label='Avec Smart Change'
                                description={
                                    <Text skeleton>
                                        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
                                        incididunt ut labore et dolore magna aliqua.
                                    </Text>
                                }
                                disabled
                            />
                            <RadioTile
                                horizontal
                                label='Sans Smart Change'
                                description={
                                    <Text skeleton>
                                        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
                                        incididunt ut labore et dolore magna aliqua.
                                    </Text>
                                }
                                disabled
                                checked
                            />
                        </RadioTiles>
                    </CategoryBox>
                </>
            )}
            <CategoryBox title='Quel forfait vous faut-il&nbsp;?'>
                {isRenewal && (
                    <>
                        <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Conserver mon forfait actuel
                        </Text>
                        <RadioTile
                            id='plan-owned'
                            label='XXXGo'
                            description={<Text skeleton>Lorem ipsum dolor sit amet</Text>}
                            disabled
                            checked
                            horizontal
                        />
                        <Divider content='Ou' />
                        <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Choisir un nouveau forfait
                        </Text>
                        <Spacer size={SpacerSize.THREE} />
                    </>
                )}
                <RadioTiles className={classNames(isRenewal ? 'plan-radio-list' : 'grid-radio-list', 'is-loading')}>
                    {generateEmptyArray(6).map((value) => (
                        <RadioTile id={`plan-${value}`} key={value} label='200Go' disabled horizontal />
                    ))}
                </RadioTiles>
                <Spacer size={SpacerSize.FIVE} />
                <div className='is-loading'>
                    <Spacer size={SpacerSize.FOUR} />
                    <Text marginless className='w-fit'>
                        Voir le détail du forfait xxxGo
                    </Text>
                    <Spacer size={SpacerSize.SIX} />
                </div>
                <Rows>
                    {type === WallType.PHONE &&
                        generateEmptyArray(2).map((value) => (
                            <Row key={value}>
                                <div className='is-loading is-flex is-flex-col'>
                                    <Text
                                        typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}
                                        className='w-fit'
                                    >
                                        Vous voulez conserver votre numéro&nbsp;?
                                    </Text>
                                    <Spacer size={SpacerSize.ONE} />
                                    <Text level={TextLevels.THREE} className='w-fit'>
                                        Bonne nouvelle&nbsp;! Je bénéficie x €/mois sur mon forfait.
                                    </Text>
                                </div>
                                <RadioTiles className='grid-radio-list is-loading'>
                                    <RadioTile id='promo-yes' label='Oui' disabled horizontal />
                                    <RadioTile id='promo-no' label='Non' disabled horizontal />
                                </RadioTiles>
                            </Row>
                        ))}
                </Rows>
            </CategoryBox>
            <CategoryBox title='Quel paiement pour votre téléphone&nbsp;?'>
                <RadioTiles className='is-loading'>
                    <RadioTile
                        label='Lorem ipsum'
                        description={
                            <Text>
                                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
                                incididunt ut labore et dolore magna aliqua.
                            </Text>
                        }
                        disabled
                        horizontal
                        checked
                    />
                    <RadioTile
                        label='Lorem ipsum'
                        description={
                            <Text>
                                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
                                incididunt ut labore et dolore magna aliqua.
                            </Text>
                        }
                        disabled
                        horizontal
                    />
                </RadioTiles>
            </CategoryBox>
        </>
    );
}

function SkeletonOffers() {
    return (
        <Box shadowless>
            <BoxContent>
                <View flexable align={Align.CENTER}>
                    <Image src={TradeInIcon} alt='' width={40} />
                    <Spacer size={SpacerSize.THREE} horizontal />
                    <Title level={TitleLevels.FOUR}>Vous avez un mobile à revendre&nbsp;?</Title>
                </View>
                <Text level={TextLevels.ONE} typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}>
                    Estimez-le au meilleur prix et bénéficiez d&apos;un bonus reprise.
                </Text>
                <div className='is-loading'>
                    <Text marginless level={TextLevels.TWO} className='w-fit'>
                        Profitez d&apos;une remise différée sur votre nouveau téléphone
                    </Text>
                    <Spacer size={SpacerSize.ONE} />
                    <Text level={TextLevels.TWO} className='w-fit'>
                        reprendre votre ancien mobile. Vous y gagnez
                    </Text>
                </div>
                <Button disabled>Estimer mon mobile</Button>
            </BoxContent>
        </Box>
    );
}

export function SkeletonTicket({
    isRenewal,
    type,
    showRef,
}: {
    isRenewal: boolean;
    type: WallType;
    showRef: RefCallback<HTMLDivElement>;
}) {
    const isAccessory = type === WallType.ACCESSORY;

    return (
        <Box shadowless className='remove-section-padding box-container'>
            <BoxContent>
                <Columns>
                    <Column>
                        <Title level={TitleLevels.FIVE}>{isRenewal ? 'Mon offre actuelle' : 'Mes achats'}</Title>
                    </Column>
                </Columns>
                <View flexable justify={Justify.SPACE_BETWEEN} className='is-loading'>
                    <Text level={TextLevels.THREE}>Téléphone vraiment turbo stylé</Text>
                    <Text level={TextLevels.THREE}>xx,xx €</Text>
                </View>
                <View flexable justify={Justify.SPACE_BETWEEN} className='is-loading'>
                    <Text level={TextLevels.THREE}>Prix à payer aujourd&apos;hui</Text>
                    <Text level={TextLevels.THREE}>x,xx €</Text>
                </View>
                <View flexable justify={Justify.SPACE_BETWEEN} className='is-loading'>
                    <Text level={TextLevels.THREE} marginless={isAccessory}>
                        dont 0,05 € d&apos;éco-participation
                    </Text>
                </View>
                {!isAccessory && (
                    <View flexable justify={Justify.SPACE_BETWEEN} className='is-loading'>
                        <Text level={TextLevels.THREE} marginless>
                            Après remboursement sur demande
                        </Text>
                        <Text level={TextLevels.THREE} marginless>
                            xx €
                        </Text>
                    </View>
                )}
                <div className='is-loading'>
                    <Spacer size={SpacerSize.FOUR} />
                    <Text marginless className='w-fit'>
                        Voir le détail du prix
                    </Text>
                </div>
                {!isAccessory && (
                    <>
                        <Divider />

                        <View flexable justify={Justify.SPACE_BETWEEN} className='is-loading'>
                            <Text level={TextLevels.THREE}>Forfait avec tout plein de giga utile</Text>
                            <Text level={TextLevels.THREE}>xx,xx € /mois</Text>
                        </View>
                        <View flexable justify={Justify.SPACE_BETWEEN} className='is-loading'>
                            <Text level={TextLevels.THREE}>Pendant 12 mois puis 39,99 €</Text>
                        </View>
                        <View flexable justify={Justify.SPACE_BETWEEN} className='is-loading'>
                            <Text level={TextLevels.THREE} marginless>
                                Engagement 2 ans
                            </Text>
                        </View>
                        <div className='is-loading'>
                            <Spacer size={SpacerSize.FOUR} />
                            <Text marginless className='w-fit'>
                                Voir le détail du forfait xxxGo
                            </Text>
                        </div>
                    </>
                )}

                <Divider />

                <div ref={showRef}>
                    <Button disabled fullwidth>
                        <Text className='is-invisible' marginless>
                            Voir les autres offres
                        </Text>
                    </Button>
                </div>
            </BoxContent>
        </Box>
    );
}

function SkeletonTabs() {
    return (
        <Tabs small>
            <TabList className='has-text-left'>
                <Tab label="Qu'est-ce qui" className='is-loading' />
                <Tab label='est jaune et' className='is-loading' />
                <Tab label='qui attend ?' className='is-loading' />
                <Tab label="Un pastis a l'apéro" className='is-loading' />
            </TabList>
        </Tabs>
    );
}

function SkeletonBreadcrumb({ type, isRenewal }: Readonly<{ type: WallType; isRenewal: boolean }>) {
    let url = '';
    let label = '';

    switch (type) {
        case WallType.PHONE:
            url = isRenewal ? paths.renewalPhoneWall : paths.phoneWall;
            label = 'Téléphone mobile';
            break;
        case WallType.ACCESSORY:
            url = paths.accessoryWall;
            label = 'Accessoires';
            break;
        case WallType.HOTSPOT:
            url = paths.accessoryWall;
            label = 'Hotspots et Tablettes';
            break;
        case WallType.SALES_PHONE:
        case WallType.SALES_ACCESSORY:
            url = paths.sales(type === WallType.SALES_PHONE ? SalesWallType.PHONE : SalesWallType.ACCESSORY);
            label = 'Bons plans';
            break;
    }

    return (
        <Breadcrumb>
            <BreadcrumbItem className='is-underline' href='/'>
                Accueil
            </BreadcrumbItem>
            <BreadcrumbItem className='is-underline' href={url}>
                {label}
            </BreadcrumbItem>
            <BreadcrumbItem active className='is-loading'>
                Pourquoi faire un skeleton
            </BreadcrumbItem>
            <BreadcrumbItem active className='is-loading'>
                vu par 1 personne ?
            </BreadcrumbItem>
        </Breadcrumb>
    );
}

function SkeletonLegals() {
    return (
        <Accordion>
            <AccordionItem id='skeletonLegals'>
                <AccordionHeader>Mentions légales</AccordionHeader>
                <AccordionBody>
                    <div className='is-loading'>
                        <Text>
                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut
                            labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                            laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in
                            voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat
                            cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                        </Text>
                    </div>
                </AccordionBody>
            </AccordionItem>
        </Accordion>
    );
}
